import { useAuth } from "@/services/auth/auth";
import {
  useGetInstitutionProfileQuery,
  useGetInstitutionSignatureStatusQuery,
} from "@/services/graphql/reactQuery";
import { useRouteContext } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { buildWorkflowUrl, isWorkflowConfigured } from "./ironclad";

export type WorkflowStatus = "loading" | "signed" | "unsigned" | "inProgress";

// Poll while unsigned so the banner is removed when a colleague at the institution signs.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

export function useIroncladWorkflow() {
  const { principalId } = useAuth();
  const { institutionId } = useRouteContext({ from: "__root__" });
  const configured = isWorkflowConfigured();
  const [hasLaunched, setHasLaunched] = useState(false);

  const query = useGetInstitutionSignatureStatusQuery(
    { institutionId },
    {
      enabled: configured && institutionId != null,
      refetchInterval: (q) => {
        const data = q.state.data?.SignatureService_getInstitutionSignatureStatus;
        return data?.signed ? false : POLL_INTERVAL_MS;
      },
      refetchOnWindowFocus: true,
      staleTime: 0,
    }
  );

  // Institution profile prefills the workflow launch form.
  const profileQuery = useGetInstitutionProfileQuery(
    { institutionId },
    {
      enabled: configured && institutionId != null,
      staleTime: Infinity,
    }
  );

  const institution = profileQuery.data?.getMarketplaceInstitution;
  const data = query.data?.SignatureService_getInstitutionSignatureStatus;

  let status: WorkflowStatus;
  if (!configured) {
    status = "signed";
  } else if (query.isLoading) {
    status = "loading";
  } else if (data?.signed) {
    status = "signed";
  } else if (data?.signatureRecord?.status?.toLowerCase() === "active") {
    status = "inProgress";
  } else {
    status = "unsigned";
  }

  const workflowUrl =
    configured && principalId
      ? buildWorkflowUrl(principalId, {
          counterpartyName: institution?.name ?? "",
          institutionId: String(institutionId),
          institutionName: institution?.name ?? "",
          "counterpartyAddress[lines][0]": institution?.address ?? "",
          "counterpartyAddress[locality]": institution?.city ?? "",
          "counterpartyAddress[region]": institution?.stateAbbrev ?? "",
          "counterpartyAddress[postcode]": institution?.zipCode ?? "",
          "counterpartyAddress[country]": "United States",
        })
      : null;

  const launch = useCallback(() => {
    if (!workflowUrl) return;
    window.open(workflowUrl, "_blank", "noopener,noreferrer");
    setHasLaunched(true);
  }, [workflowUrl]);

  return {
    status,
    hasLaunched,
    launch,
    recheck: () => void query.refetch(),
  };
}
